/**
 * v135 — SVG element → PNG blob rasterizer.
 *
 * Why this lives in its own file: the same canvas-based pipeline is reused
 * across all three card variants (build / session / compare). Keeping the
 * SVG-shaped output of ShareCardSVG.tsx pure (no canvas inside React render)
 * means the SVG element can also be embedded inline (the modal preview)
 * without paying the rasterization cost twice.
 *
 * Pipeline:
 *
 *   SVGElement → XMLSerializer string → data URL → <img> load → canvas draw
 *     → canvas.toBlob → PNG Blob → caller decides (download / Web Share /
 *     copy URL / upload).
 *
 * Caveats:
 *   - Fonts: the SVG must inline @font-face or fall back to web-safe families.
 *     Our share card uses the system "ui-monospace" + "ui-sans-serif" stack
 *     so we don't depend on any external font load.
 *   - Foreign objects (HTML in SVG) DO NOT rasterize reliably on Safari.
 *     The share card is pure SVG primitives — no foreignObject. If you add
 *     one later, expect blank output on iOS.
 *   - CORS: any image hrefs inside the SVG must be same-origin or have
 *     crossOrigin set. Photo backgrounds use data: URIs to sidestep.
 */

const PNG_WIDTH = 1200;
const PNG_HEIGHT = 630;

/** Serialize an SVG element to a string. */
export function serializeSvg(svg: SVGSVGElement): string {
  const cloned = svg.cloneNode(true) as SVGSVGElement;
  // Ensure standard namespaces are present even if React stripped them.
  if (!cloned.getAttribute('xmlns')) {
    cloned.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
  }
  if (!cloned.getAttribute('xmlns:xlink')) {
    cloned.setAttribute('xmlns:xlink', 'http://www.w3.org/1999/xlink');
  }
  return new XMLSerializer().serializeToString(cloned);
}

/**
 * Rasterize an SVG element into a PNG Blob at 1200×630.
 *
 * The promise resolves with the Blob or rejects with a descriptive error.
 * Callers typically chain into URL.createObjectURL or pass the Blob to
 * navigator.share().
 */
export async function svgToPngBlob(
  svg: SVGSVGElement,
  width: number = PNG_WIDTH,
  height: number = PNG_HEIGHT,
): Promise<Blob> {
  const xml = serializeSvg(svg);
  // Use data: URL rather than Blob URL — Blob URLs sometimes hit Safari's
  // "tainted canvas" check on subsequent toBlob() calls. data: URLs always
  // render same-origin in the canvas's eyes.
  const dataUrl = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(xml)}`;

  const img = new Image();
  img.crossOrigin = 'anonymous';

  await new Promise<void>((resolve, reject) => {
    img.onload = () => resolve();
    img.onerror = () => reject(new Error('SVG failed to load into rasterizer image'));
    img.src = dataUrl;
  });

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('2D canvas context unavailable');

  // Paint the ember-on-near-black canvas background even though the SVG
  // already has its own — guards against any blend-mode oddities on iOS.
  ctx.fillStyle = '#0a0a0a';
  ctx.fillRect(0, 0, width, height);
  ctx.drawImage(img, 0, 0, width, height);

  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) resolve(blob);
        else reject(new Error('canvas.toBlob returned null'));
      },
      'image/png',
      0.95,
    );
  });
}

/** Convenience — returns a same-process Object URL for immediate <img> use. */
export async function svgToPngObjectUrl(svg: SVGSVGElement): Promise<string> {
  const blob = await svgToPngBlob(svg);
  return URL.createObjectURL(blob);
}

/**
 * v135.2 hotfix — triggers a browser download of the rasterized PNG.
 *
 * iOS Safari quirk: `<a download>` inside an iframe is unreliable. Safari may
 * navigate to the blob URL or route the file through the system intent —
 * which, when FB Messenger is installed, can land the image in Messenger
 * instead of Files. We try Web Share API first (single file, no text/url)
 * because iOS routes that to the native share sheet where "Save to Files"
 * is a top-level option. Fall back to `<a download>` only when web-share
 * isn't available — that's the canonical desktop path.
 */
export async function downloadSvgAsPng(svg: SVGSVGElement, filename: string): Promise<void> {
  const blob = await svgToPngBlob(svg);
  const file = new File([blob], filename, {
    type: 'image/png',
    lastModified: Date.now(),
  });

  // Prefer Web Share API on platforms that support it for files.
  if (typeof navigator !== 'undefined') {
    const nav = navigator as Navigator & {
      share?: (d: object) => Promise<void>;
      canShare?: (d: { files?: File[] }) => boolean;
    };
    if (typeof nav.canShare === 'function' && nav.canShare({ files: [file] }) && typeof nav.share === 'function') {
      try {
        // Files-only share — iOS surfaces "Save to Files", "Save Image", and
        // the full app sheet; user picks where the PNG lands.
        await nav.share({ files: [file] });
        return;
      } catch (err) {
        const e = err as Error;
        // User cancelled — that's success from their perspective.
        if (e?.name === 'AbortError') return;
        // Otherwise fall through to <a download>.
      }
    }
  }

  // Fallback path — desktop browsers, or iframes without web-share permission.
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.target = '_blank';
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Defer revoke so iOS has a chance to actually fetch the blob.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
